import type {
  FilterFieldType,
  FilterMatchOperator,
  FilterOp,
  FilterOperator,
} from "./types";

/**
 * A per-type value codec. PURE — no React, no I/O. Maps a typed URL value plus
 * the active operator to a DSL fragment (server fold) and knows when a value is
 * "empty" (no fragment / skip in the matcher). The default `toApiFilter` is
 * keyed by `apiKey`; descriptors override `toApiFilter` for key renames or
 * bespoke shapes.
 */
export interface Codec {
  isEmptyValue: (value: unknown) => boolean;
  defaultToApiFilter: (
    value: unknown,
    apiKey: string,
    operator: FilterOp,
  ) => Record<string, FilterOperator>;
}

/** Normalize a `string | string[] | "" | null | undefined` value to a clean string[]. */
function toArray(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.filter((v) => v != null && v !== "").map(String);
  }
  return [String(value)];
}

/**
 * Pick the wire key for a set/equality selection. Anything outside the pair
 * falls back to the inclusive form — a field can only reach here with an
 * operator `resolveOperator` already validated against its own list, so the
 * fallback exists to keep the return type total, not to mask a real choice.
 */
function setOperatorKey(operator: FilterOp): "in" | "notIn" {
  return operator === "notIn" ? "notIn" : "in";
}

function equalityOperatorKey(operator: FilterOp): "eq" | "neq" {
  return operator === "neq" ? "neq" : "eq";
}

export const multiSelectCodec = (): Codec => ({
  isEmptyValue: (value) =>
    value == null ||
    value === "" ||
    (Array.isArray(value) &&
      value.filter((v) => v != null && v !== "").length === 0),
  defaultToApiFilter: (value, apiKey, operator) => {
    const arr = toArray(value);
    if (!arr.length) return {};
    return { [apiKey]: { [setOperatorKey(operator)]: arr.join(",") } };
  },
});

export const singleSelectCodec = (): Codec => ({
  isEmptyValue: (value) => value == null || value === "",
  defaultToApiFilter: (value, apiKey, operator) =>
    value == null || value === ""
      ? {}
      : { [apiKey]: { [equalityOperatorKey(operator)]: String(value) } },
});

// Entity = single-value (id) selection; same wire semantics as single-select.
export const entityCodec = singleSelectCodec;

// Multi-entity = multi-value (id[]) selection; same wire semantics as
// multi-select (`{ in }`), but options are fetched async + searched server-side.
export const multiEntityCodec = multiSelectCodec;

/** Coerce the six accepted boolean URL forms to a canonical boolean (or undefined for empty). */
export function normalizeBoolean(value: unknown): boolean | undefined {
  if (value === true || value === "true" || value === "1" || value === 1) {
    return true;
  }
  if (value === false || value === "false" || value === "0" || value === 0) {
    return false;
  }
  return undefined;
}

/**
 * Boolean codec, parameterized by the API's true/false representation. Customers'
 * accounts API expects the literal `"true"/"false"` (the defaults); other APIs
 * may want `"1"/"0"`.
 */
export const booleanCodec = ({
  trueValue = "true",
  falseValue = "false",
}: {
  trueValue?: string;
  falseValue?: string;
} = {}): Codec => ({
  isEmptyValue: (value) => normalizeBoolean(value) === undefined,
  defaultToApiFilter: (value, apiKey, operator) => {
    const bool = normalizeBoolean(value);
    if (bool === undefined) return {};
    return {
      [apiKey]: {
        [equalityOperatorKey(operator)]: bool ? trueValue : falseValue,
      },
    };
  },
});

type DateRangeValue = { start?: string; end?: string; label?: string };

export type PriceRangeValue = { min: number; max: number };

/**
 * Encode a two-bound range under the active operator. `range` spreads the
 * bounds over `gte` + `lte`; `between` packs them into the platform's comma
 * pair; the four comparison operators keep only the bound they read (a lower
 * bound for gt/gte, an upper bound for lt/lte), so switching operators on a
 * chip never has to discard the value the user already picked.
 */
function rangeFragment(
  operator: FilterOp,
  low: string | undefined,
  high: string | undefined,
): FilterOperator {
  switch (operator) {
    case "between":
      return low && high ? { between: `${low},${high}` } : {};
    case "gt":
    case "gte":
      return low ? { [operator]: low } : {};
    case "lt":
    case "lte":
      return high ? { [operator]: high } : {};
    default: {
      const fragment: FilterOperator = {};
      if (low) fragment.gte = low;
      if (high) fragment.lte = high;
      return fragment;
    }
  }
}

/**
 * Date-range codec. Encodes start/end under the active operator; when
 * `isNullLabel` is configured and the value's label matches the sentinel, emits
 * `{ isNull: "1" }` instead (the orders "No completed date" case).
 */
export const dateRangeCodec = ({
  isNullLabel,
}: {
  isNullLabel?: string;
} = {}): Codec => ({
  isEmptyValue: (value) => {
    if (value == null || value === "") return true;
    const v = value as DateRangeValue;
    return !v.start && !v.end && !v.label;
  },
  defaultToApiFilter: (value, apiKey, operator) => {
    if (value == null || value === "") return {};
    const v = value as DateRangeValue;
    if (isNullLabel && v.label === isNullLabel) {
      return { [apiKey]: { isNull: "1" } };
    }
    const fragment = rangeFragment(operator, v.start, v.end);
    return Object.keys(fragment).length ? { [apiKey]: fragment } : {};
  },
});

/**
 * Price-range codec. URL values are whole dollars; the API receives cents,
 * encoded under the active operator.
 */
export const priceRangeCodec = (): Codec => ({
  isEmptyValue: (value) => value == null || value === "",
  defaultToApiFilter: (value, apiKey, operator) => {
    if (value == null || value === "") return {};
    const v = value as PriceRangeValue;
    const toCents = (dollars: number) => String(Math.round(dollars * 100));
    const fragment = rangeFragment(operator, toCents(v.min), toCents(v.max));
    return Object.keys(fragment).length ? { [apiKey]: fragment } : {};
  },
});

/**
 * The client fold's single operator encoder — collapses the three operators
 * into one place. `is`/`isAnyOf` fail on a null/absent item value; `isNot`
 * passes a null item value (it is "not any of" the set).
 */
export function defaultMatchValue(
  itemValue: string | null | undefined,
  value: unknown,
  operator: FilterMatchOperator,
): boolean {
  const values = toArray(value);
  switch (operator) {
    case "is":
      return itemValue != null && itemValue === values[0];
    case "isAnyOf":
      return itemValue != null && values.includes(itemValue);
    case "isNot":
      return itemValue == null || !values.includes(itemValue);
    default:
      return true;
  }
}

/** Default codec per field type (used when a field declares no `toApiFilter`). */
export const codecForType: Record<FilterFieldType, Codec> = {
  multiSelect: multiSelectCodec(),
  singleSelect: singleSelectCodec(),
  entity: entityCodec(),
  multiEntity: multiEntityCodec(),
  boolean: booleanCodec(),
  dateRange: dateRangeCodec(),
  priceRange: priceRangeCodec(),
};
